const EMPTY_VALUES = new Set(["", "null", "undefined"]);

const isEmptyVariantValue = (value) => {
  if (value === null || value === undefined) return true;
  if (typeof value === "object" && !Array.isArray(value)) {
    const rawVal = value.label || value.name || value.value || value.text || null;
    if (rawVal !== null) {
      const strRaw = String(rawVal).trim().toLowerCase();
      if (EMPTY_VALUES.has(strRaw) || strRaw.startsWith("no-")) return true;
    }
  }
  const str = String(value).trim().toLowerCase();
  return EMPTY_VALUES.has(str) || str.startsWith("no-");
};

const getRawLabel = (value) => {
  if (isEmptyVariantValue(value)) return "";
  if (typeof value === "object") {
    return (
      value.label ||
      value.name ||
      value.value ||
      value.text ||
      ""
    );
  }
  return value;
};

const stripStoredPrice = (value) => {
  const label = getRawLabel(value);
  if (isEmptyVariantValue(label)) return "";
  return String(label).split(",")[0].trim();
};

const firstLabel = (...values) => {
  for (const value of values) {
    const label = stripStoredPrice(value);
    if (label) return label;
  }
  return "";
};

const normalizePercent = (value) => {
  if (isEmptyVariantValue(value)) return "";
  const normalized = String(value).trim().replace(/%$/, "");
  if (!normalized || normalized === "100") return "";
  return normalized;
};

const firstPercent = (...values) => {
  for (const value of values) {
    const percent = normalizePercent(value);
    if (percent) return percent;
  }
  return "";
};

const getLabelList = (value) => {
  if (isEmptyVariantValue(value)) return [];
  if (Array.isArray(value)) {
    return value.map(stripStoredPrice).filter(Boolean);
  }
  return String(value)
    .split("|")
    .map(stripStoredPrice)
    .filter(Boolean);
};

const getToppingLabels = (topping) => {
  return getLabelList(topping);
};

const normalizeIds = (value) => {
  if (isEmptyVariantValue(value)) return [];
  return Array.isArray(value) ? value : [value];
};

const getSelectedSettingLabels = (selectedIds, settings) => {
  const ids = normalizeIds(selectedIds).map((id) => String(id));
  if (!ids.length || !Array.isArray(settings)) return [];

  return settings
    .filter((setting) => ids.includes(String(setting.id)))
    .map(stripStoredPrice)
    .filter(Boolean);
};

const getSelectedAttributeLabels = (selectedAttributes = {}) => {
  if (!selectedAttributes || typeof selectedAttributes !== "object") return [];

  return Object.entries(selectedAttributes)
    .filter(([key]) => !["size"].includes(key))
    .map(([, attribute]) => stripStoredPrice(attribute))
    .filter(Boolean);
};

export const buildOrderVariantBadges = (order = {}) => {
  const badges = [];
  const sizeFromSettings = getSelectedSettingLabels(
    order.sizeSelectedId,
    order.sizeSettings,
  )[0];
  const flavorFromSettings = getSelectedSettingLabels(
    order.flavorSelectedId,
    order.flavorSettings,
  )[0];
  const toppingFromSettings = getSelectedSettingLabels(
    order.toppingSelectedId,
    order.toppingSettings,
  );
  const size = firstLabel(
    order.size,
    order.sizeName,
    order.selectedAttributes?.size,
    sizeFromSettings,
  );
  const flavor = firstLabel(order.flavor, order.flavorName, flavorFromSettings);
  const ice = firstPercent(order.ice, order.iceSelectedValue);
  const sugar = firstPercent(order.sugar, order.sugarSelectedValue);
  const toppingLabels = [
    ...new Set([
      ...getToppingLabels(order.topping),
      ...getLabelList(order.toppingName),
      ...toppingFromSettings,
    ]),
  ];
  const selectedAttributeLabels = getSelectedAttributeLabels(
    order.selectedAttributes,
  );

  if (size) badges.push(size);
  if (flavor) badges.push(flavor);
  badges.push(...selectedAttributeLabels);
  badges.push(...toppingLabels);
  if (ice) badges.push(`${ice}% đá`);
  if (sugar) badges.push(`${sugar}% ngọt`);

  return badges;
};

export const getOrderVariantSignature = (order = {}) =>
  buildOrderVariantBadges(order).join("|");
